import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import MainLayout from '../layouts/MainLayout';

const Login = lazy(() => import('../pages/Login'));
const Dashboard = lazy(() => import('../pages/Dashboard'));
const Anchors = lazy(() => import('../pages/Anchors'));
const AnchorDetail = lazy(() => import('../pages/AnchorDetail'));
const Products = lazy(() => import('../pages/Products'));
const Samples = lazy(() => import('../pages/Samples'));
const Bindings = lazy(() => import('../pages/Bindings'));
const SampleRegister = lazy(() => import('../pages/SampleRegister'));
const ProductAnalysis = lazy(() => import('../pages/ProductAnalysis'));
const Settings = lazy(() => import('../pages/Settings'));
const OceanEngine = lazy(() => import('../pages/OceanEngine'));
const FeishuImport = lazy(() => import('../pages/FeishuImport'));
const CreatorWorkbench = lazy(() => import('../pages/CreatorWorkbench'));
const TimeoutRules = lazy(() => import('../pages/TimeoutRules'));
const ScriptOrders = lazy(() => import('../pages/ScriptOrders'));
const FeeSyncWorkbench = lazy(() => import('../pages/FeeSyncWorkbench'));
const FeeOwnerConfig = lazy(() => import('../pages/FeeOwnerConfig'));
const ScriptTimeoutRules = lazy(() => import('../pages/ScriptTimeoutRules'));
const ProductImport = lazy(() => import('../pages/ProductImport'));
const BrandConfig = lazy(() => import('../pages/BrandConfig'));
const BrandSyncConfig = lazy(() => import('../pages/BrandSyncConfig'));
const PartnerChannels = lazy(() => import('../pages/PartnerChannels'));
const SyncConfigGroups = lazy(() => import('../pages/SyncConfigGroups'));
const SyncMappingTemplates = lazy(() => import('../pages/SyncMappingTemplates'));
const Workload = lazy(() => import('../pages/Workload'));
const WorkloadDashboard = lazy(() => import('../pages/WorkloadDashboard'));
const VideoImportRecords = lazy(() => import('../pages/VideoImportRecords'));
const VideoSampleReconciliation = lazy(() => import('../pages/VideoSampleReconciliation'));
const InvestmentHistory = lazy(() => import('../pages/InvestmentHistory'));
const BusinessRules = lazy(() => import('../pages/BusinessRules'));
const SamplePolicySettings = lazy(() => import('../pages/SamplePolicySettings'));
const CreatorLeads = lazy(() => import('../pages/CreatorLeads'));
const MerchantLeads = lazy(() => import('../pages/MerchantLeads'));
const OperationLogs = lazy(() => import('../pages/OperationLogs'));
const LegacySampleBinding = lazy(() => import('../pages/LegacySampleBinding'));
const TodayWorkbench = lazy(() => import('../pages/TodayWorkbench'));
const SyncConfigOnboarding = lazy(() => import('../pages/SyncConfigOnboarding'));
const BrandSyncStatus = lazy(() => import('../pages/BrandSyncStatus'));
const BrandSyncCenter = lazy(() => import('../pages/BrandSyncCenter'));
const PublicPool = lazy(() => import('../pages/PublicPool'));
const BrandWorkspaceLayout = lazy(() => import('../pages/BrandWorkbench').then((module) => ({ default: module.BrandWorkspaceLayout })));
const BrandLibrary = lazy(() => import('../pages/BrandWorkbench').then((module) => ({ default: module.BrandLibrary })));
const BrandOverview = lazy(() => import('../pages/BrandWorkbench').then((module) => ({ default: module.BrandOverview })));
const BrandProducts = lazy(() => import('../pages/BrandWorkbench').then((module) => ({ default: module.BrandProducts })));
const BrandProductDetail = lazy(() => import('../pages/BrandWorkbench').then((module) => ({ default: module.BrandProductDetail })));
const BrandBatches = lazy(() => import('../pages/BrandWorkbench').then((module) => ({ default: module.BrandBatches })));
const BrandLogisticsReview = lazy(() => import('../pages/BrandWorkbench').then((module) => ({ default: module.BrandLogisticsReview })));
const AdminBatchCenter = lazy(() => import('../pages/BrandWorkbench').then((module) => ({ default: module.AdminBatchCenter })));
const BrandSamples = lazy(() => import('../pages/BrandSamples').then((module) => ({ default: module.BrandSamples })));
const MySamples = lazy(() => import('../pages/BrandSamples').then((module) => ({ default: module.MySamples })));
const AdminSamples = lazy(() => import('../pages/BrandSamples').then((module) => ({ default: module.AdminSamples })));

import { ProtectedRoute, PublicOnlyRoute, RequirePoolDomain, RequireRole, getAuthRoles } from '../auth/RouteGuards';
import { useAuth } from '../auth/AuthContext';

function BindingsCompatibility() {
  const { currentUser } = useAuth();
  const roles = getAuthRoles(currentUser);
  const crossDomain = roles.has('bd') && (roles.has('creator') || roles.has('supervisor'));
  const target = roles.has('admin')
    ? '/public-pool'
    : roles.has('bd') && !crossDomain
      ? '/merchant-relations'
      : !roles.has('bd') && (roles.has('creator') || roles.has('supervisor'))
        ? '/public-pool'
        : '/dashboard';
  return <Navigate to={target} replace />;
}

export default function AppRouter() {
  return (
    <Suspense fallback={<div className="page-loading">页面加载中...</div>}>
    <Routes>
      <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
      <Route path="/" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="workbench" element={<CreatorWorkbench />} />
        <Route path="today-workbench" element={<TodayWorkbench />} />
        <Route path="anchors" element={<Anchors />} />
        <Route path="my-anchors" element={<Anchors />} />
        <Route path="creator-leads" element={<CreatorLeads />} />
        <Route path="merchant-leads" element={<RequirePoolDomain domain="merchant"><MerchantLeads /></RequirePoolDomain>} />
        <Route path="operation-logs" element={<RequireRole roles={['admin']}><OperationLogs /></RequireRole>} />
        <Route path="admin/legacy-sample-binding" element={<RequireRole roles={['admin']}><LegacySampleBinding /></RequireRole>} />
        <Route path="public-pool" element={<RequirePoolDomain domain="creator"><PublicPool /></RequirePoolDomain>} />
        <Route path="anchors/:id" element={<AnchorDetail />} />
        <Route path="products" element={<Products />} />
        <Route path="samples" element={<Samples />} />
        <Route path="video-import-records" element={<VideoImportRecords />} />
        <Route path="video-sample-reconciliation" element={<VideoSampleReconciliation />} />
        <Route path="investment-history" element={<InvestmentHistory />} />
        <Route path="merchant-relations" element={<RequireRole roles={['bd']}><Bindings /></RequireRole>} />
        <Route path="bindings" element={<BindingsCompatibility />} />
        <Route path="sample-register" element={<SampleRegister />} />
        <Route path="analysis" element={<ProductAnalysis />} />
        <Route path="oceanengine" element={<RequireRole roles={['admin']}><OceanEngine /></RequireRole>} />
        <Route path="settings" element={<RequireRole roles={['admin']}><Settings /></RequireRole>} />
        <Route path="timeout-rules" element={<RequireRole roles={['admin']}><TimeoutRules /></RequireRole>} />
        <Route path="script-orders" element={<ScriptOrders />} />
        <Route path="fee-sync" element={<FeeSyncWorkbench />} />
        <Route path="fee-owner-config" element={<RequireRole roles={['admin', 'supervisor']}><FeeOwnerConfig /></RequireRole>} />
        <Route path="script-timeout-rules" element={<RequireRole roles={['admin']}><ScriptTimeoutRules /></RequireRole>} />
        <Route path="product-import" element={<RequireRole roles={['admin']}><ProductImport /></RequireRole>} />
        <Route path="brand-config" element={<RequireRole roles={['admin']}><BrandConfig /></RequireRole>} />
        <Route path="brand-sync-config" element={<BrandSyncConfig />} />
        <Route path="brand-sync-status" element={<BrandSyncStatus />} />
        <Route path="brand-workbench" element={<BrandLibrary />} />
        <Route path="brand-workbench/:merchantId" element={<BrandWorkspaceLayout />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<BrandOverview />} />
          <Route path="products" element={<BrandProducts />} />
          <Route path="products/:productId" element={<BrandProductDetail />} />
          <Route path="samples" element={<BrandSamples />} />
          <Route path="batches" element={<BrandBatches />} />
          <Route path="logistics-review" element={<BrandLogisticsReview />} />
          <Route path="sync-config" element={<RequireRole roles={['admin']}><BrandSyncCenter /></RequireRole>} />
        </Route>
        <Route path="my-samples" element={<MySamples />} />
        <Route path="admin/samples" element={<RequireRole roles={['admin']}><AdminSamples /></RequireRole>} />
        <Route path="admin/sample-batches" element={<RequireRole roles={['admin']}><AdminBatchCenter /></RequireRole>} />
        <Route path="admin/batches" element={<Navigate to="/admin/sample-batches" replace />} />
        <Route path="admin/partner-channels" element={<RequireRole roles={['admin']}><PartnerChannels /></RequireRole>} />
        <Route path="admin/sync-config-groups" element={<RequireRole roles={['admin']}><SyncConfigGroups /></RequireRole>} />
        <Route path="admin/sync-mapping-templates" element={<RequireRole roles={['admin']}><SyncMappingTemplates /></RequireRole>} />
        <Route path="admin/sync-config-onboarding" element={<RequireRole roles={['admin']}><SyncConfigOnboarding /></RequireRole>} />
        <Route path="business-rules" element={<RequireRole roles={['admin']}><BusinessRules /></RequireRole>} />
        <Route path="sample-policy-settings" element={<RequireRole roles={['admin']}><SamplePolicySettings /></RequireRole>} />
        <Route path="workload" element={<Workload />} />
        <Route path="workload-dashboard" element={<WorkloadDashboard />} />
        <Route path="feishu-import" element={<RequireRole roles={['admin']}><FeishuImport /></RequireRole>} />
      </Route>
    </Routes>
    </Suspense>
  );
}
